import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ShieldOff } from 'lucide-react';
import { BizwebEntityType, AccessRuleType } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AccessRuleGroupsManager } from '~/components/access-rules/access-rule-groups-manager';
import { serverMessage } from '~/lib/api/server-error';
import { customerAccessRulesApi } from '~/lib/api/client';
import { useCustomerGroupsList } from '~/hooks/use-customer-groups';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

// Reviews have no per-entity anchor, so review exclusions live as global CustomerAccessRule rows
// with EntityType=Review and this sentinel EntityId, all RuleType=Deny (a blacklist).
const REVIEW_ENTITY_ID = 0;
const reviewExcludedKeys = ['review-excluded-groups'] as const;

/**
 * Manages the customer groups whose members are blocked from creating product reviews.
 * Backed by CustomerAccessRule(EntityType=Review, EntityId=0, RuleType=Deny).
 */
export function ReviewExcludedGroupsDialog({ open, onOpenChange }: Props) {
	const { t } = useTranslation();
	const qc = useQueryClient();
	const { data: groups } = useCustomerGroupsList();

	const { data, isLoading } = useQuery({
		queryKey: reviewExcludedKeys,
		// GetByEntity may return rows of either RuleType; reviews are exclude-only, so keep Deny rows defensively (matches legacy admin).
		queryFn: async () =>
			((await customerAccessRulesApi.getByEntity(REVIEW_ENTITY_ID, BizwebEntityType.Review)).data.items ?? []).filter(
				r => r.RuleType === AccessRuleType.Deny
			),
		enabled: open
	});

	const invalidate = () => qc.invalidateQueries({ queryKey: reviewExcludedKeys });
	const create = useMutation({
		mutationFn: async (customerGroupId: number) =>
			(
				await customerAccessRulesApi.insert({
					EntityType: BizwebEntityType.Review,
					EntityId: REVIEW_ENTITY_ID,
					CustomerGroupId: customerGroupId,
					RuleType: AccessRuleType.Deny
				})
			).data,
		onSuccess: invalidate
	});
	const remove = useMutation({ mutationFn: async (id: number) => (await customerAccessRulesApi.remove(id)).data, onSuccess: invalidate });

	const add = (customerGroupId: number) =>
		create.mutate(customerGroupId, { onSuccess: () => toast.success(t('common.save')), onError: e => toast.error(serverMessage(e)) });
	const del = (id: number) =>
		remove.mutate(id, { onSuccess: () => toast.success(t('common.delete')), onError: e => toast.error(serverMessage(e)) });

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-[95vw] sm:max-w-xl">
				<DialogHeader>
					<DialogTitle>{t('catalog.reviews.excludedGroups.title')}</DialogTitle>
					<DialogDescription>{t('catalog.reviews.excludedGroups.subtitle')}</DialogDescription>
				</DialogHeader>

				<AccessRuleGroupsManager
					rows={data ?? []}
					groups={groups ?? []}
					isLoading={isLoading}
					labelPrefix="catalog.reviews.excludedGroups"
					emptyIcon={<ShieldOff size={32} className="text-foreground-disabled" strokeWidth={1.25} />}
					isAdding={create.isPending}
					onAdd={add}
					onRemove={del}
				/>
			</DialogContent>
		</Dialog>
	);
}
